import { isEmpty } from "lodash";

import { appstr } from "@coreapp/utils/appstr";
import { enumRuleErrorType, QRulePair } from "@coreapp/CoreApp/QRulePair";
import { QRule } from "@coreapp/CoreApp/QRule";

export type ErrorDisplay = {
  hasError: boolean;
  errorString?: string;
  errorType?: string;
};

export type UIRuleError = {
  fieldError: Record<number, ErrorDisplay>;
  valueError: Record<number, ErrorDisplay>;
  generalRuleError: Record<number, ErrorDisplay>;
  hasError: boolean;
};

// Errors are keyed by rule-pair index, not by UID, because the QRule-based path
// (Custom Chart modal, Chart Scope, View Rule Editor) stores pairs as an ordered
// list with no stable ID. If a pair is removed between submit and re-edit, indices
// shift and a stale error can map to the wrong pair — it will correct itself on the
// next submit. The RSB path keys by fUID (useChartRuleEditorController) and avoids
// this; basic rules have at most 2 pairs so the practical impact is minimal.
export const intersectErrors = (
  submitted: UIRuleError,
  current: UIRuleError
): UIRuleError | null => {
  const fieldError: Record<number, ErrorDisplay> = {};
  const valueError: Record<number, ErrorDisplay> = {};
  const generalRuleError: Record<number, ErrorDisplay> = {};

  for (const key in submitted.fieldError) {
    const k = Number(key);
    if (current.fieldError[k]?.hasError) fieldError[k] = submitted.fieldError[k];
  }
  for (const key in submitted.generalRuleError) {
    const k = Number(key);
    const currentGen = current.generalRuleError[k];
    const submittedGen = submitted.generalRuleError[k];
    const sameError = currentGen?.hasError && currentGen.errorString === submittedGen.errorString;
    // BothValuesEmpty persists while the user is still fixing: filling one value transitions
    // the error type to ValueEmpty, but the "both fields required" message should stay until
    // both are filled (ValueEmpty → None). Only clears on InvalidNumericRange/InvalidDateRange
    // (both filled but invalid) or None (both filled and valid).
    const bothValuesPartiallyFixed =
      submittedGen.errorType === enumRuleErrorType.BothValuesEmpty &&
      currentGen?.errorType === enumRuleErrorType.ValueEmpty;
    if (sameError || bothValuesPartiallyFixed) {
      generalRuleError[k] = submittedGen;
      if (submitted.valueError[k]) valueError[k] = submitted.valueError[k];
    }
  }

  const hasError = !isEmpty(fieldError) || !isEmpty(valueError) || !isEmpty(generalRuleError);
  return hasError ? { fieldError, valueError, generalRuleError, hasError } : null;
};

const applyRuleError = (errorType: string, index: number, errorObject: UIRuleError): void => {
  switch (errorType) {
    case enumRuleErrorType.InvalidDateRange:
      errorObject.generalRuleError[index] = {
        hasError: true,
        errorString: appstr("Invalid date range"),
        errorType: enumRuleErrorType.InvalidDateRange
      };
      errorObject.valueError[index] = { hasError: true };
      break;
    case enumRuleErrorType.InvalidNumericRange:
      errorObject.generalRuleError[index] = {
        hasError: true,
        errorString: appstr("Invalid numeric range"),
        errorType: enumRuleErrorType.InvalidNumericRange
      };
      errorObject.valueError[index] = { hasError: true };
      break;
    case enumRuleErrorType.ValueEmpty:
      errorObject.generalRuleError[index] = {
        hasError: true,
        errorString: appstr("This field is required"),
        errorType: enumRuleErrorType.ValueEmpty
      };
      errorObject.valueError[index] = { hasError: true };
      break;
    case enumRuleErrorType.BothValuesEmpty:
      errorObject.generalRuleError[index] = {
        hasError: true,
        errorString: appstr("Both fields are required"),
        errorType: enumRuleErrorType.BothValuesEmpty
      };
      errorObject.valueError[index] = { hasError: true };
      break;
    case enumRuleErrorType.FieldEmpty:
      errorObject.fieldError[index] = {
        hasError: true,
        errorString: appstr("This field is required")
      };
      break;
    // ComparisonEmpty is intentionally not handled: the UI always sets a default
    // comparison value, so this state is unreachable through normal interaction.
  }
};

const makeEmptyError = (): UIRuleError => ({
  hasError: false,
  fieldError: {},
  valueError: {},
  generalRuleError: {}
});

const finalizeError = (errorObject: UIRuleError): UIRuleError => {
  if (
    !isEmpty(errorObject.fieldError) ||
    !isEmpty(errorObject.valueError) ||
    !isEmpty(errorObject.generalRuleError)
  ) {
    errorObject.hasError = true;
  }
  return errorObject;
};

export const computeRulePairErrors = (rulePair: QRulePair): UIRuleError => {
  const errorObject = makeEmptyError();
  applyRuleError(rulePair.GetErrorType(), 0, errorObject);
  return finalizeError(errorObject);
};

export const computeErrors = (rule: QRule): UIRuleError => {
  const errorObject = makeEmptyError();

  if (rule.IsNoRule()) {
    return errorObject;
  }

  if (rule.GetIsComplex()) {
    const def = rule.GetComplexDef()?.trim();
    if (!def) {
      errorObject.generalRuleError[0] = {
        hasError: true,
        errorString: appstr("This field is required")
      };
      errorObject.hasError = true;
      return errorObject;
    }
    // Validate the expression syntax (eval-based check in QRule.ValidateRule).
    const result = rule.ValidateRule();
    if (result !== "success") {
      errorObject.generalRuleError[0] = { hasError: true, errorString: result };
      errorObject.hasError = true;
    }
    return errorObject;
  }

  rule.GetRulePairList()?.forEach((rp: QRulePair, index: number) => {
    applyRuleError(rp.GetErrorType(), index, errorObject);
  });

  return finalizeError(errorObject);
};
